import { useFormik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxes, FaBoxOpen, FaBuilding, FaChartLine, FaCog, FaCube, FaEdit, FaEuroSign, FaEye, FaFileExcel, FaPlus, FaQrcode, FaSave, FaSearch, FaTag, FaTimes, FaTrash, FaUser } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Table, ListGroup, ListGroupItem, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, CardHeader, InputGroup, Pagination, PaginationItem, PaginationLink, FormFeedback, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { stockActions } from '../../sagas/stockSlice';
import { supplierActions } from '../../sagas/supplierSlice';
import { productActions } from '../../sagas/productSlice';
import { debounce } from 'lodash';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';


const StockPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [priceFilter, setPriceFilter] = useState('');
    const { products, loading: productsLoading, totalPages: productsTotalPages, totalElements: productsTotalElements } = useSelector(state => state.products);
    const { suppliers, loading: suppliersLoading, totalPages: suppliersTotalPages, totalElements: suppliersTotalElements } = useSelector(state => state.suppliers);
    const { stocks, loading: stockLoading, totalPages: stockTotalPages, totalElements: stockTotalElements } = useSelector(state => state.stock);
    const [editedPrices, setEditedPrices] = useState({});
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [modal, setModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedStockEntry, setSelectedStockEntry] = useState(null);
    const [productModal, setProductModal] = useState(false);
    const [supplierModal, setSupplierModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [userPrice, setUserPrice] = useState('');
    const [newSupplierModal, setNewSupplierModal] = useState(false);
    const [newSupplier, setNewSupplier] = useState({ name: '', contact: '', address: '' });
    const [stockFilter, setStockFilter] = useState('');
    const [searchParams, setSearchParams] = useState({
        query: '',
        page: 1,
        size: 6
    });
    const [productsPagination, setProductsPagination] = useState({ currentPage: 1, pageSize: 10 });

    const [suppliersPagination, setSuppliersPagination] = useState({ currentPage: 1, pageSize: 10 });
    const [editingId, setEditingId] = useState(null);
    const [editQuantity, setEditQuantity] = useState(0);
    const [updating, setUpdating] = useState(false);
    const searchInputRef = useRef(null);
    const [addStockModal, setAddStockModal] = useState(false);
    const [searchSupplierQuery, setSearchSupplierQuery] = useState('');
    const [isSupplierSearching, setIsSupplierSearching] = useState(false);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);

    // Add handler for updating stock
    const handleUpdateStock = async (stock) => {
        setUpdating(true);
        try {
            await dispatch(stockActions.updateStock({
                id: stock.id,
                stock: { ...stock, quantity: editQuantity },
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('stock.updateSuccess'),
                        text: t('stock.quantityUpdated'),
                        showConfirmButton: false,
                        timer: 1500
                    }).then(() => {
                        setEditingId(null);
                        setEditQuantity(0);
                        dispatch(stockActions.search({
                            productId: selectedProduct.id,
                            page: 1,
                            size: 10,
                            onSuccess: () => {
                                // Additional success handling if needed
                            }
                        }));
                    });


                    // Show success message

                }
            }));

        } catch (error) {
            console.error('Failed to update stock:', error);
            // Show error message
            Swal.fire({
                icon: 'error',
                title: t('stock.updateError'),
                text: t('stock.updateErrorMessage'),
            });
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteStock = async (stockId) => {
        try {
            const result = await Swal.fire({
                icon: 'warning',
                title: t('stock.deleteConfirmTitle'),
                text: t('stock.deleteConfirmMessage'),
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('common.confirm'),
                cancelButtonText: t('common.cancel')
            });

            if (result.isConfirmed) {
                await dispatch(stockActions.deleteStock(
                    {
                        id: stockId,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('stock.deleteSuccess'),
                                text: t('stock.deleteSuccessMessage'),
                                showConfirmButton: false,
                                timer: 1500
                            }).then(() => {
                                dispatch(stockActions.search({
                                    productId: selectedProduct.id,
                                    page: 1,
                                    size: 10,
                                    onSuccess: () => {
                                        // Additional success handling if needed
                                    }
                                }));
                            });
                        }
                    }));
            }
        } catch (error) {
            console.error('Failed to delete stock:', error);
            Swal.fire({
                icon: 'error',
                title: t('stock.deleteError'),
                text: t('stock.deleteErrorMessage')
            });
        }
    };

    const toggleModal = () => setModal(!modal);

    const handleViewStockEntries = (product) => {
        setSelectedProduct(product);
        dispatch(stockActions.search({
            productId: product.id,
            page: 1,
            size: 10,
            onSuccess: () => {
                // Additional success handling if needed
            }
        }));
        toggleModal();
    };

    const handleEditStockEntry = (stockEntry) => {
        setSelectedStockEntry(stockEntry);
        // Populate form with stockEntry data
        // ...existing form handling code...
    };

    const loadStockEntries = useCallback(() => {
        dispatch(stockActions.search({
            ...searchParams,
            onSuccess: () => setIsSearching(false)
        }));
    }, [dispatch, searchParams]);

    const debouncedSearch = useCallback(
        debounce((value) => {
            dispatch(stockActions.search({
                ...searchParams,
                query: value,
                page: 1
            }));
        }, 500),
        [searchParams, dispatch]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchParams((prev) => ({
            ...prev,
            query: value,
            page: 1
        }));
        debouncedSearch(value);
    };

    const loadProducts = useCallback(() => {
        dispatch(productActions.search({
            criteria: { query: searchQuery, supplierId: selectedSupplier ? selectedSupplier.id : null },
            page: productsPagination.currentPage,
            size: productsPagination.pageSize
        }));
    }, [dispatch, searchQuery, productsPagination, selectedSupplier]);

    useEffect(() => {
        let payload = {
            criteria: { query: searchQuery, supplierId: selectedSupplier ? selectedSupplier.id : null },
            page: productsPagination.currentPage,
            size: 10,
        };
        dispatch(productActions.search(payload));
    }, []);


    useEffect(() => {
        let payload = {
            criteria: { query: searchQuery, supplierId: selectedSupplier ? selectedSupplier.id : null },
            page: productsPagination.currentPage,
            size: 10,
        };
        dispatch(productActions.search(payload));
    }, [selectedSupplier]);

    const handleProductPageChange = (page) => {
        setProductsPagination(prev => ({ ...prev, currentPage: page }));
        dispatch(productActions.search({
            criteria: { query: searchQuery, supplierId: selectedSupplier ? selectedSupplier.id : null },
            page,
            size: productsPagination.pageSize
        }));
    };

    const handlePageChange = (page) => {
        setSearchParams(prev => ({ ...prev, page }));
        dispatch(stockActions.search({
            ...searchParams,
            page,
            onSuccess: () => setIsSearching(false)
        }));
    };
    const handleSupplierPageChange = (page) => {
        setSuppliersPagination(prev => ({ ...prev, currentPage: page }));
        dispatch(supplierActions.search({
            query: searchSupplierQuery,
            page,
            size: suppliersPagination.pageSize
        }));
    };

    const validationSchema = Yup.object({
        product: Yup.object().required(t('validation.required')),
        // supplier: Yup.object().required(t('validation.required')),
        buyingPrice: Yup.number()
            .required(t('validation.required'))
            .min(0, t('validation.positive')),
        salePrice: Yup.number()
            .required(t('validation.required'))
            .min(0, t('validation.positive')),
        quantity: Yup.number()
            .required(t('validation.required'))
            .min(1, t('validation.minQuantity'))
    });


    const formik = useFormik({
        initialValues: {
            product: null,
            supplier: null,
            buyingPrice: '',
            salePrice: '',
            quantity: ''
        },
        validationSchema,
        onSubmit: (values) => {
            Swal.fire({
                title: t('stock.confirmSave'),
                text: t('stock.confirmSaveMessage'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: t('common.confirm'),
                cancelButtonText: t('common.cancel')
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(stockActions.addStock({
                        ...values,
                        onSuccess: () => {
                            Swal.fire({
                                icon: 'success',
                                title: t('stock.saved'),
                                timer: 1500,
                                showConfirmButton: false
                            }).then(() => {
                                formik.resetForm();
                                toggleAddStockModal();
                                loadProducts(); // Add this line
                            });

                        },
                        onError: (error) => {
                            Swal.fire({
                                icon: 'error',
                                title: t('common.error'),
                                text: error.message || t('stock.saveError')
                            });
                        }
                    }));
                }
            });
        }
    });

    const handleProductSelect = (product) => {
        formik.setFieldValue('product', product);
        setSelectedProduct(product)
        if (product) {
            formik.setFieldValue('buyingPrice', product.buyingPrice || 0);
            formik.setFieldValue('salePrice', product.userPrice || 0);
        } else {
            formik.setFieldValue('buyingPrice', '');
            formik.setFieldValue('salePrice', '');
        }
        setProductModal(false);
        setAddStockModal(true); // Open the add stock modal

    };

    const toggleAddStockModal = () => {
        setAddStockModal(!addStockModal);
    };

    const handleSupplierSelect = (supplier) => {
        if (selectedProduct) {
            formik.setFieldValue('supplier', supplier);
        } else {
            setSelectedSupplier(supplier);
        }
        setSupplierModal(false);
    };

    const handleOpenSupplierModal = () => {
        dispatch(supplierActions.search({
            query: '',
            page: 0,
            size: 10
        }));
        setSupplierModal(true);

    };


    const debouncedProductSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(productActions.search({
                criteria: { query, supplierId: selectedSupplier ? selectedSupplier.id : null },
                page: 1,
                size: 10,
                onSuccess: () => setIsSearching(false)
            }));
        }, 500), // Wait 500ms after last keystroke
        [selectedSupplier]
    );

    const debouncedSupplierSearch = useCallback(
        debounce((query) => {
            setIsSupplierSearching(true);
            dispatch(supplierActions.search({
                query,
                page: 0,
                size: 8,
                onSuccess: () => setIsSupplierSearching(false)
            }));
        }, 1500), // Wait 500ms after last keystroke
        []
    );

    const handleProductSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedProductSearch(value);
    };

    const handleSupplierSearchChange = (e) => {
        const value = e.target.value;
        setSearchSupplierQuery(value);
        debouncedSupplierSearch(value);
      };


    const handleNewSupplierSubmit = () => {
        dispatch(supplierActions.addSupplier({
            ...newSupplier,
            onSuccess: () => {
                setNewSupplierModal(false);
                dispatch(supplierActions.search({ page: 0, size: 10 }));
                Swal.fire({
                    icon: 'success',
                    title: t('supplier.addSuccess'),
                    timer: 1500
                });
            }
        }));
    };

    const handleCloseSupplierModal = () => {
        setSupplierModal(false);
        setSearchSupplierQuery('');
    };

    const handleExportExcel = () => {
        Swal.fire({
            title: t('product.exportLoading'),
            text: t('product.exportWait'),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        let payload = {
            onSuccess: (data) => {
                const excelData = data.map(produit => ({
                    'Code': produit.code,
                    'Description': produit.description,
                    'Qté en stock': produit.stock
                }));

                const ws = XLSX.utils.json_to_sheet(excelData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Produits');

                const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([excelBuffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const fileName = `produits_${new Date().toISOString().split('T')[0]}.xlsx`;
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();

                Swal.fire({
                    title: t('product.exportSuccessTitle'),
                    text: t('product.exportSuccessText'),
                    icon: 'success',
                    timer: 1500
                });
            },
            onError: (error) => {
                Swal.fire({
                    title: t('product.exportErrorTitle'),
                    text: t('product.exportErrorText'),
                    icon: 'error'
                });
            }
        };

        dispatch(productActions.findAll(payload));
    };

    const handleClearSupplierFilter = () => {
        setSelectedSupplier(null);
        setProductsPagination({ ...productsPagination, currentPage: 1 });
        dispatch(productActions.search({
            criteria: { query: searchTerm },
            page: 1,
            size: productsPagination.pageSize
        }));
    };

    return (
        <Container fluid>
            <Row>
                <Col md={12}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaBoxes className="text-primary me-2" />
                                    {t('stock.pageTitle')}
                                </h4>
                            </div>

                            {productsLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (<>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Search Input - Left Side */}
                                    <div className="position-relative flex-grow-1 me-3">
                                        <Input
                                            innerRef={searchInputRef}
                                            type="text"
                                            placeholder={t('product.searchFocusHint')}
                                            value={searchTerm}
                                            onChange={handleProductSearchChange}
                                            className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                            title={t('product.searchShortcut')}
                                        />
                                        <FaSearch
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={14}
                                        />
                                        <FaBarcode
                                            style={{
                                                position: 'absolute',
                                                right: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={20}
                                            title={t('product.scanHint')}
                                        />
                                    </div>
                                    <div className="me-2">
                                        {selectedSupplier ? (
                                            <Button color="info" size="sm" onClick={handleClearSupplierFilter}>
                                                {selectedSupplier.description} <FaTimes className="ms-1" />
                                            </Button>
                                        ) : (
                                            <Button color="secondary" size="sm" onClick={handleOpenSupplierModal}>
                                                {t('stock.selectSupplier')}
                                            </Button>
                                        )}
                                    </div>
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={handleExportExcel}
                                        className="d-flex align-items-center"
                                    >
                                        <FaFileExcel className="me-2" />
                                        {t('common.exportExcel')}
                                    </Button>
                                </div>
                                <div className="table-responsive" style={{
                                    height: '600px',
                                    overflowY: 'auto',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                }}>
                                    <Table bordered hover>
                                        <thead>
                                            <tr>
                                                {/* <th>{t('product.lastUpdated')}</th> */}
                                                <th className="align-middle">
                                                    <FaQrcode className="me-2 text-primary" size={12} />
                                                    {t('product.barcode')}
                                                </th>
                                                <th className="align-middle text-center">
                                                    <FaBox className="me-2 text-primary" size={12} />
                                                    {t('product.description')}
                                                </th>
                                                <th className="align-middle text-center">
                                                    <FaBoxOpen className="me-2 text-primary" size={12} />
                                                    {t('product.stockQty')}
                                                </th>
                                                <th className="align-middle text-center" style={{ width: '120px' }}>
                                                    <FaCog className="me-2 text-primary" size={12} />
                                                    {t('common.actions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!products || products.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {t('product.noProducts')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (products.map((product) => (
                                                <tr key={product.id}
                                                    onClick={() => handleProductSelect(product)}
                                                    className={`cursor-pointer ${selectedProduct?.id === product.id ? 'table-active' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {/* <td>{product.updatedAt}</td> */}
                                                    <td>{product.barcode}</td>
                                                    <td>{product.description}</td>
                                                    {/* <td>{product.barcode}</td> */}
                                                    <td>{product.stock}</td>

                                                    <td className="text-center">
                                                        <Button
                                                            color="info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleViewStockEntries(product);
                                                            }}
                                                        >
                                                            <FaEye size={16} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        {t('product.showing', {
                                            start: (productsPagination.currentPage - 1) * productsPagination.pageSize + 1,
                                            end: Math.min(productsPagination.currentPage * productsPagination.pageSize, productsTotalElements),
                                            total: productsTotalElements,
                                            currentPage: productsPagination.currentPage,
                                            totalPages: productsTotalPages
                                        })}
                                    </small>
                                    <Pagination size="sm" className="mb-0">
                                        <PaginationItem disabled={productsPagination.currentPage === 1}>
                                            <PaginationLink first onClick={() => handleProductPageChange(1)}>
                                                <FaAngleDoubleLeft size={10} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={productsPagination.currentPage === 1}>
                                            <PaginationLink previous onClick={() => handleProductPageChange(productsPagination.currentPage - 1)}>
                                                <FaAngleLeft size={10} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={productsPagination.currentPage === productsTotalPages}>
                                            <PaginationLink next onClick={() => handleProductPageChange(productsPagination.currentPage + 1)}>
                                                <FaAngleRight size={10} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={productsPagination.currentPage === productsTotalPages}>
                                            <PaginationLink last onClick={() => handleProductPageChange(productsTotalPages)}>
                                                <FaAngleDoubleRight size={10} />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            </>)}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {selectedProduct && (
                <Modal isOpen={modal} toggle={toggleModal} size="lg" onClosed={() => {
                    // Reload products list
                    dispatch(productActions.search({
                        criteria: { query: searchQuery },
                        page: productsPagination.currentPage,
                        size: productsPagination.pageSize
                    }));
                }}>
                    <ModalHeader toggle={toggleModal}>
                        {t('stock.stockEntriesFor')} {selectedProduct.description}
                    </ModalHeader>
                    <ModalBody>
                        <Card className="shadow-sm h-100">
                            <CardHeader className="bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <FaBoxes className="text-primary me-2" />
                                        <h5 className="mb-0">{t('stock.entries')}</h5>
                                    </div>
                                    <div className="search-wrapper" style={{ width: '250px' }}>
                                        <Input
                                            type="text"
                                            placeholder={t('stock.searchEntries')}
                                            value={stockFilter}
                                            onChange={handleSearchChange}
                                            className="form-control-sm"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody className="p-0">
                                {!stocks || stocks.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FaBoxes className="text-muted mb-3" size={48} />
                                        <h5 className="text-muted mb-0">{t('stock.noEntries')}</h5>
                                        <p className="text-muted small">{t('stock.noEntriesSubtext')}</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive" style={{ maxHeight: '500px' }}>
                                            <Table hover bordered >
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="text-center">{t('stock.entryDate')}</th> {/* New Header */}
                                                        <th>{t('stock.product')}</th>
                                                        {/* <th className="text-end">{t('stock.buyingPrice')}</th>
                                                        <th className="text-end">{t('stock.salePrice')}</th> */}
                                                        <th className="text-center">{t('stock.quantity')}</th>
                                                        <th>{t('stock.supplier')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stockLoading ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4">
                                                                <Spinner size="sm" />
                                                            </td>
                                                        </tr>
                                                    ) : stocks.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <FaBoxes size={32} className="mb-2" />
                                                                    <p className="mb-0">{t('stock.noEntries')}</p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        stocks.map((entry, index) => (
                                                            <tr key={index}>
                                                                <td className="text-center">{entry.createdAt}</td> {/* New Data Column */}
                                                                <td>{entry.product.description}</td>
                                                                <td className="text-center">
                                                                    {editingId === entry.id ? (
                                                                        <div className="d-flex align-items-center">
                                                                            <Input
                                                                                type="number"
                                                                                value={editQuantity}
                                                                                onChange={(e) => setEditQuantity(parseInt(e.target.value))}
                                                                                className="me-2"
                                                                                style={{ width: '100px' }}
                                                                            />
                                                                            <Button
                                                                                color="success"
                                                                                size="sm"
                                                                                onClick={() => handleUpdateStock(entry)}
                                                                                disabled={updating}
                                                                            >
                                                                                {updating ? (
                                                                                    <Spinner size="sm" />
                                                                                ) : (
                                                                                    <FaSave />
                                                                                )}
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="d-flex align-items-center">
                                                                            {entry.quantity}
                                                                            <Button
                                                                                color="link"
                                                                                size="sm"
                                                                                className="ms-2 p-0"
                                                                                onClick={() => {
                                                                                    setEditingId(entry.id);
                                                                                    setEditQuantity(entry.quantity);
                                                                                }}
                                                                            >
                                                                                <FaEdit />
                                                                            </Button>
                                                                        </div>
                                                                    )}

                                                                </td>
                                                                <td>{entry.supplier ? entry.supplier.description : t('stock.noSupplier')}</td>
                                                                <td>
                                                                    <Button
                                                                        color="danger"
                                                                        size="sm"
                                                                        className="ms-2"
                                                                        onClick={() => handleDeleteStock(entry.id)}
                                                                        title={t('stock.delete')}
                                                                    >
                                                                        <FaTrash />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>

                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <small className="text-muted">
                                                {t('product.showing', {
                                                    start: (searchParams.page - 1) * searchParams.size + 1,
                                                    end: Math.min(searchParams.page * searchParams.size, stockTotalElements),
                                                    total: stockTotalElements,
                                                    currentPage: searchParams.page,
                                                    totalPages: stockTotalPages
                                                })}
                                            </small>
                                            <Pagination size="sm" className="mb-0">
                                                <PaginationItem disabled={searchParams.page === 1}>
                                                    <PaginationLink first onClick={() => handlePageChange(searchParams.page - 1)}>
                                                        <FaAngleDoubleLeft size={10} />
                                                    </PaginationLink>
                                                </PaginationItem>
                                                <PaginationItem disabled={searchParams.page === 1}>
                                                    <PaginationLink previous onClick={() => handlePageChange(searchParams.page - 1)}>
                                                        <FaAngleLeft size={10} />
                                                    </PaginationLink>
                                                </PaginationItem>
                                                <PaginationItem disabled={searchParams.page === stockTotalPages}>
                                                    <PaginationLink next onClick={() => handlePageChange(searchParams.page + 1)}>
                                                        <FaAngleRight size={10} />
                                                    </PaginationLink>
                                                </PaginationItem>
                                                <PaginationItem disabled={searchParams.page === stockTotalPages}>
                                                    <PaginationLink last onClick={() => handlePageChange(stockTotalPages)}>
                                                        <FaAngleDoubleRight size={10} />
                                                    </PaginationLink>
                                                </PaginationItem>
                                            </Pagination>
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </ModalBody>
                </Modal>
            )}

            {/* New Supplier Modal */}
            <Modal isOpen={newSupplierModal} toggle={() => setNewSupplierModal(false)}>
                <ModalHeader toggle={() => setNewSupplierModal(false)}>
                    {t('supplier.new')}
                </ModalHeader>
                <ModalBody>
                    {/* <FormGroup>
                        <Label>{t('supplier.code')}</Label>
                        <Input
                            value={newSupplier.code}
                            onChange={e => setNewSupplier({ ...newSupplier, code: e.target.value })}
                        />
                    </FormGroup> */}
                    <FormGroup>
                        <Label>{t('supplier.description')}</Label>
                        <Input
                            value={newSupplier.description}
                            onChange={e => setNewSupplier({ ...newSupplier, description: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{t('supplier.contact')}</Label>
                        <Input
                            value={newSupplier.contact}
                            onChange={e => setNewSupplier({ ...newSupplier, contact: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{t('supplier.address')}</Label>
                        <Input
                            type="textarea"
                            value={newSupplier.address}
                            onChange={e => setNewSupplier({ ...newSupplier, address: e.target.value })}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleNewSupplierSubmit}>
                        {t('common.save')}
                    </Button>
                    <Button color="secondary" onClick={() => setNewSupplierModal(false)}>
                        {t('common.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Product Selection Modal */}
            <Modal isOpen={productModal} toggle={() => setProductModal(false)}>
                <ModalHeader toggle={() => setProductModal(false)}>
                    {t('stock.selectProduct')}
                </ModalHeader>
                <ModalBody>
                    {productsLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('common.loading')}</span>
                            </div>
                            <div className="mt-2 text-muted">
                                {t('common.loadingData')}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                {/* Search Input - Left Side */}
                                <div className="position-relative flex-grow-1 me-3">
                                    <Input
                                        type="text"
                                        placeholder={t('product.searchFocusHint')}
                                        value={searchTerm}
                                        onChange={handleProductSearchChange}
                                        className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                        title={t('product.searchShortcut')}
                                    />
                                    <FaSearch
                                        style={{
                                            position: 'absolute',
                                            left: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#adb5bd'
                                        }}
                                        size={14}
                                    />
                                    <FaBarcode
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#adb5bd'
                                        }}
                                        size={20}
                                        title={t('product.scanHint')}
                                    />
                                </div>
                            </div>
                            <div className="table-responsive">
                                <Table bordered hover>
                                    <thead>
                                        <tr>
                                            <th>{t('product.code')}</th>
                                            <th>{t('product.description')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!products || products.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    <div className="text-muted">
                                                        {t('product.noProducts')}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (products.map((product) => (
                                            <tr key={product.id} onClick={() => handleProductSelect(product)}>
                                                <td>{product.code}</td>
                                                <td>{product.description}</td>

                                            </tr>
                                        ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                    {t('product.showing', {
                                        start: (productsPagination.currentPage - 1) * productsPagination.pageSize + 1,
                                        end: Math.min(productsPagination.currentPage * productsPagination.pageSize, productsTotalElements),
                                        total: productsTotalElements,
                                        currentPage: productsPagination.currentPage,
                                        totalPages: productsTotalElements
                                    })}
                                </small>
                                <Pagination size="sm" className="mb-0">
                                    <PaginationItem disabled={productsPagination.currentPage === 1}>
                                        <PaginationLink first onClick={() => handleProductPageChange(productsPagination.currentPage - 1)}>
                                            <FaAngleDoubleLeft size={10} />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={productsPagination.currentPage === 1}>
                                        <PaginationLink previous onClick={() => handleProductPageChange(productsPagination.currentPage - 1)}>
                                            <FaAngleLeft size={10} />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={productsPagination.currentPage === productsTotalPages}>
                                        <PaginationLink next onClick={() => handleProductPageChange(productsPagination.currentPage + 1)}>
                                            <FaAngleRight size={10} />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={productsPagination.currentPage === productsTotalPages}>
                                        <PaginationLink last onClick={() => handleProductPageChange(productsTotalPages)}>
                                            <FaAngleDoubleRight size={10} />
                                        </PaginationLink>
                                    </PaginationItem>
                                </Pagination>
                            </div>
                        </>)}
                </ModalBody>
            </Modal>

            <Modal isOpen={supplierModal} toggle={handleCloseSupplierModal}>
                <ModalHeader toggle={handleCloseSupplierModal}>
                    {t('stock.selectSupplier')}
                </ModalHeader>
                <ModalBody>

                    {suppliersLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('common.loading')}</span>
                            </div>
                            <div className="mt-2 text-muted">
                                {t('common.loadingData')}
                            </div>
                        </div>
                    ) : (<>
                        <div className="d-flex justify-content-between mb-3">
                            <Input
                                placeholder={t('stock.searchSupplier')}
                                value={searchSupplierQuery}
                                className={`${isSupplierSearching ? 'searching' : ''}`}
                                onChange={e => handleSupplierSearchChange(e)}
                            />

                            <Button
                                color="primary"
                                className="ms-2"
                                onClick={() => setNewSupplierModal(true)}
                            >
                                <FaPlus className="me-1" />
                                {t('supplier.new')}
                            </Button>

                        </div>
                        <div className="table-responsive">
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th>{t('supplier.code')}</th>
                                        <th>{t('supplier.description')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!suppliers || suppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="text-muted">
                                                    {t('product.noProducts')}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (suppliers.map((supplier) => (
                                        <tr key={supplier.id} onClick={() => handleSupplierSelect(supplier)}>
                                            <td>{supplier.code}</td>
                                            <td>{supplier.description}</td>
                                        </tr>
                                    ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <small className="text-muted">
                                {t('product.showing', {
                                    start: (suppliersPagination.currentPage - 1) * suppliersPagination.pageSize + 1,
                                    end: Math.min(suppliersPagination.currentPage * suppliersPagination.pageSize, suppliersTotalElements),
                                    total: suppliersTotalElements,
                                    currentPage: suppliersPagination.currentPage,
                                    totalPages: suppliersTotalPages
                                })}
                            </small>
                            <Pagination size="sm" className="mb-0">
                                <PaginationItem disabled={suppliersPagination.currentPage === 1}>
                                    <PaginationLink first onClick={() => handleSupplierPageChange(suppliersPagination.currentPage > 0 ? suppliersPagination.currentPage - 1 : 0)}>
                                        <FaAngleDoubleLeft size={12} />
                                    </PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={suppliersPagination.currentPage === 1}>
                                    <PaginationLink previous onClick={() => handleSupplierPageChange(suppliersPagination.currentPage > 0 ? suppliersPagination.currentPage - 1 : 0)}>
                                        <FaAngleLeft size={12} />
                                    </PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={suppliersPagination.currentPage === suppliersTotalPages}>
                                    <PaginationLink next onClick={() => handleSupplierPageChange(suppliersPagination.currentPage + 1)}>
                                        <FaAngleRight size={12} />
                                    </PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={suppliersPagination.currentPage === suppliersTotalPages}>
                                    <PaginationLink last onClick={() => handleSupplierPageChange(suppliersTotalPages)}>
                                        <FaAngleDoubleRight size={12} />
                                    </PaginationLink>
                                </PaginationItem>
                            </Pagination>
                        </div>
                    </>)}
                </ModalBody>
            </Modal>
            <Modal isOpen={addStockModal} toggle={toggleAddStockModal}>
                <ModalHeader toggle={toggleAddStockModal}>
                    {t('stock.addEntry')}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={formik.handleSubmit}>
                        <Row className="g-3">
                            <Col md={10}>
                                <FormGroup>
                                    <Label className="d-flex align-items-center mb-1">
                                        <FaCube className="me-1" size={10} />
                                        {t('stock.product')}
                                    </Label>
                                    <Input
                                        name="product"
                                        value={formik.values.product?.description || ''}
                                        placeholder={t('stock.selectProduct')}
                                        readOnly
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={10}>
                                <FormGroup>
                                    <Label className="d-flex align-items-center mb-1">
                                        <FaBox className="me-1" size={10} />
                                        {t('stock.quantity')}
                                    </Label>
                                    <Input
                                        type="number"
                                        name="quantity"
                                        value={formik.values.quantity}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={t('stock.enterQuantity')}
                                        invalid={formik.touched.quantity && formik.errors.quantity}
                                    />
                                    {formik.touched.quantity && formik.errors.quantity && (
                                        <FormFeedback>{formik.errors.quantity}</FormFeedback>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={10}>
                                <FormGroup>
                                    <Label className="d-flex align-items-center mb-1">
                                        <FaUser className="me-1" size={10} />
                                        {t('stock.supplier')}
                                    </Label>
                                    <InputGroup>
                                        <Input
                                            name="supplier"
                                            value={formik.values.supplier?.description || ''}
                                            placeholder={t('stock.selectSupplier')}
                                            readOnly
                                            invalid={formik.touched.supplier && formik.errors.supplier}
                                        />
                                        <Button onClick={handleOpenSupplierModal}>
                                            <FaSearch /></Button>
                                    </InputGroup>
                                    {formik.touched.supplier && formik.errors.supplier && (
                                        <FormFeedback>{formik.errors.supplier}</FormFeedback>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button
                            color="primary"
                            size="sm"
                            type="submit"
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <FaPlus className="me-1" size={10} />
                                    {t('common.save')}
                                </>
                            )}
                        </Button>
                    </Form>
                </ModalBody>
            </Modal>

        </Container>
    );
};

export default StockPage;